import { useContext, useEffect, useMemo, useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'

import { TableContext } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/TablePenetapan/TableContext'
import { assetUrl } from 'utils/url'
import { OnboardingContext } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/Onboarding/OnboardingContext'
import {
  ONBOARDING_STEP_INDEXES,
  STEP_IDS,
} from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/Onboarding/steps'

import type { RequestSendPeriodikRatingToEkin } from 'types/kinerja-tim-penilai-ps'

export const CONFIRMATION_TEXT =
  'SAYA SETUJU UNTUK MENGIRIM KE PEJABAT PENILAI KINERJA'

export type ActionBulkSendToPPKOptions = {
  onSuccess?: () => void
  onError?: () => void
}

type ActionBulkSendToPPKProps = {
  isSubmitting?: boolean
  onSubmit?: (
    payload: RequestSendPeriodikRatingToEkin,
    options?: ActionBulkSendToPPKOptions
  ) => void
  onCloseSuccessDialog?: () => void
}

export function ActionBulkSendToPPK({
  isSubmitting,
  onSubmit,
  onCloseSuccessDialog,
}: ActionBulkSendToPPKProps) {
  const context = useContext(TableContext)
  const { stepIndex, onboardingMode } = useContext(OnboardingContext)
  const [numOfSelectedReadyToSendIds, setNumOfSelectedReadyToSendIds] =
    useState<number>(0)

  // Ensure that the component is used within the correct context provider
  if (!context)
    throw new Error(
      '<TablePegawaiPenetapan.ActionBulkSendToPPK> must be used within a <TablePegawaiPenetapan.Provider>'
    )

  // ------------------------------------
  // Get context data and its derivatives
  // ------------------------------------
  const {
    pengawasSekolahList,
    selectedIds,
    onSetHasOngoingAction,
    onToggleAll,
  } = context
  const selectedReadyToSendIds = useMemo(() => {
    if (selectedIds.size <= 0) return []
    return pengawasSekolahList
      .filter(
        (item) =>
          selectedIds.has(item.pengawasSekolahId) &&
          ['NOT_SENT', 'ERROR_SEND'].includes(item.status) &&
          Boolean(item?.predikatKinerjaTk?.predikatKinerja) &&
          item?.predikatKinerjaTk?.predikatKinerja !== 'UNDEFINED'
      )
      .map((item) => item.pengawasSekolahId)
  }, [pengawasSekolahList, selectedIds])
  const isDisabled = useMemo(() => {
    if (
      onboardingMode &&
      stepIndex !== ONBOARDING_STEP_INDEXES.SEND_PENILAIAN
    ) {
      return true
    }

    return (
      !onboardingMode &&
      (selectedReadyToSendIds.length <= 0 ||
        selectedReadyToSendIds.length !== selectedIds.size)
    )
  }, [
    onboardingMode,
    selectedIds.size,
    selectedReadyToSendIds.length,
    stepIndex,
  ])

  // --------------------------
  // Handle confirmation dialog
  // --------------------------
  const [step, setStep] = useState<
    'CONFIRMATION' | 'SUBMITTING' | 'SUCCESS' | 'IDLE'
  >('IDLE')
  const handleClose = () => {
    // Hide confirmation dialog
    setStep('IDLE')

    // Notify parent that there is no ongoing action
    onSetHasOngoingAction(false)
  }
  const handleCloseAndReset = () => {
    handleClose()
    onCloseSuccessDialog?.()
  }
  const handleOpen = () => {
    // Show confirmation dialog
    setStep('CONFIRMATION')

    // Notify parent that there is an ongoing action
    onSetHasOngoingAction(true)
  }

  // ------------------
  // Handle submission
  // ------------------
  const handleSubmit = () => {
    onSubmit?.(
      { pengawasSekolahIds: selectedReadyToSendIds },
      {
        onSuccess: () => {
          // Unchecked all checkboxes
          onToggleAll(false)

          // Show success dialog
          setStep('SUCCESS')
        },
        onError: () => setStep('IDLE'),
      }
    )
  }

  useEffect(() => {
    if (selectedReadyToSendIds.length === 0) {
      return
    }
    setNumOfSelectedReadyToSendIds(selectedReadyToSendIds.length)
  }, [selectedReadyToSendIds])

  useEffect(() => {
    if (isSubmitting) {
      setStep('SUBMITTING')
    }
  }, [isSubmitting])

  return (
    <>
      <Button
        id={STEP_IDS.SEND_PENILAIAN_ID}
        color="blue"
        disabled={isDisabled}
        className="space-x-1"
        onClick={handleOpen}
      >
        <Icon color={isDisabled ? 'disabled' : 'inverse'}>send</Icon>
        <span>Kirim ke Pejabat Penilai Kinerja</span>
      </Button>

      <Dialog
        isOpen={['CONFIRMATION', 'SUBMITTING', 'SUCCESS'].includes(step)}
        closeOnOverlayClick={false}
        size="sm"
        onClose={handleCloseAndReset}
      >
        <DialogOverlay />
        <DialogContent>
          {step === 'CONFIRMATION' && (
            <StepConfirmation onNext={handleSubmit} onPrev={handleClose} />
          )}

          {step === 'SUBMITTING' && <StepSending />}

          {step === 'SUCCESS' && (
            <StepSuccess
              count={numOfSelectedReadyToSendIds}
              onConfirm={handleCloseAndReset}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

type StepConfirmationProps = {
  isLoading?: boolean
  onPrev?: () => void
  onNext?: () => void
}
function StepConfirmation({
  isLoading,
  onPrev,
  onNext,
}: StepConfirmationProps) {
  const [confirmation, setConfirmation] = useState('')

  const isEnabled = (confirmation ?? '').toUpperCase() === CONFIRMATION_TEXT

  return (
    <div className="p-6 space-y-4">
      <img
        src={assetUrl('/illustrations/img-publish-data.png')}
        alt=""
        width={84}
        height={84}
      />

      <Text variant="display-sm" as="h2">
        Konfirmasi untuk Kirim Rekomendasi Predikat Kinerja
      </Text>

      <Text variant="body-lg" color="subdued" className="mt-2">
        Data tidak dapat diubah lagi. Pejabat Penilai Kinerja akan menerima
        rekomendasi Anda dan melanjutkan proses.
      </Text>
      <div className="mt-4">
        <Text
          variant="body"
          color="subdued"
          as="label"
          htmlFor="confirmation-text"
        >
          Mohon ketik kalimat ini pada kolom kalimat persetujuan:
        </Text>
        <div className="p-1 mt-3 rounded select-none bg-blue-5 text-blue-60">
          <Text variant="heading-sm" color="inherit">
            {CONFIRMATION_TEXT}
          </Text>
        </div>
      </div>
      <div className="max-w-[70%]">
        <Text variant="body">Kalimat persetujuan</Text>
        <Textarea
          className="mt-1 uppercase"
          placeholder="Ketik di sini..."
          autoComplete="off"
          value={confirmation}
          onChange={(e) => setConfirmation(e?.target?.value ?? '')}
        />
      </div>

      <div className="flex space-x-3 !justify-start">
        <Button
          color="white"
          size="lg"
          disabled={isLoading}
          onClick={onPrev}
          className="w-40"
        >
          Kembali
        </Button>
        <Button
          color="blue"
          disabled={!isEnabled || isLoading}
          isLoading={isLoading}
          size="lg"
          type="button"
          onClick={() => onNext?.()}
          className="w-40"
        >
          Konfirmasi
        </Button>
      </div>
    </div>
  )
}

type StepSendingProps = {}
function StepSending({}: StepSendingProps) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-9">
      <div
        className="mt-6"
        style={
          {
            '--lg-size': '72px',
            '--lg-border-size': '16px',
          } as React.CSSProperties
        }
      >
        <Spinner size="lg" />
      </div>
      <div className="text-center mt-9 w-96">
        <Text variant="display-sm">Mengirim ke Pejabat Penilai Kinerja...</Text>
        <Text variant="body-lg" color="subdued">
          Mohon tunggu sebentar, ya.
        </Text>
      </div>
    </div>
  )
}

type StepSuccessProps = {
  count: number
  onConfirm: () => void
}
function StepSuccess({ count, onConfirm }: StepSuccessProps) {
  return (
    <div className="p-6">
      <img
        src={assetUrl('/illustrations/img-thumbs-up.png')}
        alt=""
        width={84}
        height={84}
        className="mb-4"
      />

      <div className="mb-6">
        <Text variant="display-sm" as="h2" className="mb-2">
          {count} Rekomendasi Predikat Kinerja berhasil dikirim ke Pejabat
          Penilai Kinerja
        </Text>

        <Text variant="body-lg" color="subdued">
          Anda dapat melanjutkan pengiriman jika ada yang belum dikirim. Mohon
          cek secara berkala.
        </Text>
      </div>

      <Button
        color="blue"
        size="lg"
        type="button"
        onClick={onConfirm}
        className="w-48"
      >
        Saya mengerti
      </Button>
    </div>
  )
}
